import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import withAppStore from '../../../util/withAppStore'
import { DecimalInput, isNumber } from '../Inputs/Inputs.jsx'
import styles from '../../../../css/settings.module.css'

const Calibrate = ({ devices }) => {
  const [calibrate1, setCalibrate1] = useState(100)
  const [calibrate2, setCalibrate2] = useState(200)
  const [deviceCalibrateSettings, setDeviceCalibrateSettings] = useState([])
  const [calibrate1Invalid, setCalibrate1Invalid] = useState(false)
  const [calibrate2Invalid, setCalibrate2Invalid] = useState(false)

  // Initialize device calibrate settings
  useEffect(() => {
    const deviceCalibrateSettings = []
    const testSettings = devices[0].application_settings && devices[0].application_settings.settings
    const testSettingsNew = devices[0].application_settings_new && devices[0].application_settings_new.settings
    let firstCalibrate1 = 100
    if (testSettingsNew && isNumber(devices[0].application_settings_new.settings.calibrate1) && devices[0].application_settings_new.status === 'new') {
      firstCalibrate1 = devices[0].application_settings_new.settings.calibrate1
    } else {
      if (testSettings && isNumber(devices[0].application_settings.settings.calibrate1)) {
        firstCalibrate1 = devices[0].application_settings.settings.calibrate1
      }
    }
    let firstCalibrate2 = 200
    if (testSettingsNew && isNumber(devices[0].application_settings_new.settings.calibrate2) && devices[0].application_settings_new.status === 'new') {
      firstCalibrate2 = devices[0].application_settings_new.settings.calibrate2
    } else {
      if (testSettings && isNumber(devices[0].application_settings.settings.calibrate2)) {
        firstCalibrate2 = devices[0].application_settings.settings.calibrate2
      }
    }

    for (let i = 0; i < devices.length; i += 1) {
      const device = devices[i]
      deviceCalibrateSettings.push({
        address: device.address,
        status: 'new',
        settings: {
          calibrate1: firstCalibrate1,
          calibrate2: firstCalibrate2,
        },
      })
    }

    setDeviceCalibrateSettings(deviceCalibrateSettings)
    setCalibrate1(firstCalibrate1)
    setCalibrate2(firstCalibrate2)
  }, [devices])

  const appDispatch = useCallback(() => {
    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: deviceCalibrateSettings,
    })
    AppDispatcher.dispatch({
      action: 'invalid-setting',
      invalid: calibrate1Invalid || calibrate2Invalid,
    })
  }, [deviceCalibrateSettings, calibrate1Invalid, calibrate2Invalid])

  useEffect(() => {
    appDispatch()
  }, [deviceCalibrateSettings, calibrate1Invalid, calibrate2Invalid])

  const childChange = useCallback((event) => {
    handleChange(event.target.name, event.target.value)
  }, [])

  const handleChange = useCallback((field, value, callback) => {
    const newDeviceCalibrateSettings = []
    let calibrate1Invalid = true
    let calibrate2Invalid = true

    const localCalibrate1 = field === 'calibrate1' ? value : calibrate1
    const localCalibrate2 = field === 'calibrate2' ? value : calibrate2

    let calibrate1Clean = null
    if (
      isNumber(localCalibrate1) &&
      localCalibrate1 <= 3000 &&
      localCalibrate1 > 0
    ) {
      calibrate1Clean = Math.round(Number(localCalibrate1) * 10) / 10
    }

    let calibrate2Clean = null
    if (
      isNumber(localCalibrate2) &&
      localCalibrate2 <= 3000 &&
      localCalibrate2 > 0
    ) {
      calibrate2Clean = Math.round(Number(localCalibrate2) * 10) / 10
    }

    if (
      calibrate1Clean !== null &&
      calibrate2Clean !== null &&
      calibrate1Clean > calibrate2Clean
    ) {
      calibrate1Invalid = true
      calibrate2Invalid = true
    } else if (
      calibrate1Clean !== null &&
      calibrate2Clean !== null &&
      (calibrate2Clean - calibrate1Clean) >= 0.1
    ) {
      calibrate1Invalid = false
      calibrate2Invalid = false
    } else if (
      calibrate1Clean === null &&
      calibrate2Clean !== null
    ) {
      calibrate1Invalid = true
      calibrate2Invalid = false
    } else if (
      calibrate1Clean !== null &&
      calibrate2Clean === null
    ) {
      calibrate1Invalid = false
      calibrate2Invalid = true
    } else {
      calibrate1Invalid = true
      calibrate2Invalid = true
    }

    let i = 0
    const len = deviceCalibrateSettings.length

    for (i; i < len; i += 1) {
      newDeviceCalibrateSettings.push(deviceCalibrateSettings[i])
      if (!calibrate1Invalid && !calibrate2Invalid) {
        newDeviceCalibrateSettings[i].settings.calibrate1 = calibrate1Clean
        newDeviceCalibrateSettings[i].settings.calibrate2 = calibrate2Clean
      }
    }

    let saveValue = value
    const periodLocation = value.indexOf('.')
    if (periodLocation > -1) {
      if (value.slice(-1) === '.' || isNaN(value.slice(-1))) {
        // do nothing
      } else {
        saveValue = value.substring(0, periodLocation + 2)
      }
    }

    setDeviceCalibrateSettings(newDeviceCalibrateSettings)
    setCalibrate1Invalid(calibrate1Invalid)
    setCalibrate2Invalid(calibrate2Invalid)

    if (field === 'calibrate1') {
      setCalibrate1(saveValue)
    } else if (field === 'calibrate2') {
      setCalibrate2(saveValue)
    }
  }, [deviceCalibrateSettings, calibrate1, calibrate2])

  let calibrate1Style = styles.temperature
  let calibrate2Style = styles.temperature

  if (calibrate1Invalid) {
    calibrate1Style = `${styles.temperature} ${styles.inputIssue}`
  }
  if (calibrate2Invalid) {
    calibrate2Style = `${styles.temperature} ${styles.inputIssue}`
  }

  return (
    <div className={styles.inputArea}>
      <section>
        <h5>
          Calibrate 1
        </h5>
        <div className={calibrate1Style}>
          <DecimalInput
            value={calibrate1}
            childChange={childChange}
            field='calibrate1'
            style={styles.temperatureInput}
          />
        </div>
      </section>
      <section>
        <h5>
          Calibrate 2
        </h5>
        <div className={calibrate2Style}>
          <DecimalInput
            value={calibrate2}
            childChange={childChange}
            field='calibrate2'
            style={styles.temperatureInput}
          />
        </div>
      </section>
    </div>
  )
}

Calibrate.propTypes = {
  devices: PropTypes.array.isRequired,
  tempConv: PropTypes.string.isRequired,
}

export default withAppStore(Calibrate)
